import { computed, ref } from 'vue';

import { prefStorage } from '@/consent/pref-storage';
import en from './locales/en_GB.json';

/**
 * Translation, and the one place a player-facing string is allowed to live.
 *
 * ── Why the bundle is here and not on the server ────────────────────────────
 *
 * The template ships a complete i18n module (`backend/src/core/i18n/`), and it
 * loads exactly the nine files in its own `locales/` directory — there is no
 * hook for a game to contribute keys, and `core/**` is template-owned, so
 * adding `game.*` to those files would conflict on every merge from the
 * `template` remote. The core bundle therefore keeps its own strings (payment
 * errors, validation) and this file owns everything the game says.
 *
 * The backend follows from that: it never sends prose. Rank names, hull names,
 * livery names and order titles cross the wire as **keys** (`ranks.cadet.name`,
 * `shipyard.hull.destroyer_t1.name`), and the screen that draws them calls
 * `t()`. One consequence worth stating: a translation is never stale, because
 * there is no copy of it in the database to go stale.
 *
 * ── Adding a language ───────────────────────────────────────────────────────
 *
 * 1. Copy `locales/en_GB.json` to `locales/<code>.json` — the same code the
 *    backend uses, e.g. `lv_LV`, `de_DE`. Translate the values; leave the keys.
 * 2. Nothing else. `import.meta.glob` finds the file, so there is no registry
 *    to update and no import to add. A key you have not translated falls back
 *    to English rather than disappearing.
 * 3. The locale must also be in the backend's `LOCALES` env var for the
 *    account's language dropdown to offer it (`GET /api/i18n/config`).
 *
 * English is bundled statically on purpose: it is the fallback for every other
 * locale, and the first paint must never be a screen of raw keys.
 */

type Node = string | { [key: string]: Node };
type Bundle = Record<string, Node>;

/** Every non-English bundle, loaded on demand. */
const lazyBundles = import.meta.glob<{ default: Bundle }>('./locales/*.json');

export const FALLBACK_LOCALE = 'en_GB';

/** Where the last choice is remembered, so a reload does not flash English. */
const LOCALE_KEY = 'admiralty.locale';

const bundles = new Map<string, Bundle>([[FALLBACK_LOCALE, en as Bundle]]);
const locale = ref(FALLBACK_LOCALE);
/** Bumped when a bundle arrives, so every `t()` in the tree re-evaluates. */
const revision = ref(0);

/**
 * The locale to start with, before the account has been fetched.
 *
 * Order: what this browser chose last time, then what the browser itself asks
 * for, then English. The read goes through `prefStorage`, which guards a blocked
 * store and holds the choice in memory until the visitor allows preferences.
 */
function initialLocale(): string {
  const stored = prefStorage.get(LOCALE_KEY);
  if (stored) return stored;
  return matchNavigator() ?? FALLBACK_LOCALE;
}

/**
 * The best available locale for this browser's language list.
 *
 * Matches on the language subtag rather than the whole tag, so a browser set
 * to `de-AT` gets `de_DE` instead of English. Region-exact wins when offered.
 */
function matchNavigator(): string | null {
  const known = knownLocales();
  for (const tag of navigator.languages ?? [navigator.language]) {
    if (!tag) continue;
    const normalised = tag.replace('-', '_');
    const exact = known.find((code) => code.toLowerCase() === normalised.toLowerCase());
    if (exact) return exact;
    const language = normalised.split('_')[0]!.toLowerCase();
    const loose = known.find((code) => code.split('_')[0]!.toLowerCase() === language);
    if (loose) return loose;
  }
  return null;
}

/** Locale codes with a bundle on disk, derived from the filenames. */
function knownLocales(): string[] {
  return Object.keys(lazyBundles)
    .map((path) => path.slice('./locales/'.length, -'.json'.length))
    .sort();
}

async function loadBundle(code: string): Promise<void> {
  if (bundles.has(code)) return;
  const loader = lazyBundles[`./locales/${code}.json`];
  if (!loader) return; // Unknown locale: English stays, which is the fallback anyway.
  try {
    const mod = await loader();
    bundles.set(code, mod.default);
  } catch {
    /* A bundle that will not parse is a broken translation, not a broken app. */
  }
}

/**
 * Switches language. Resolves once the bundle is in memory, so a caller that
 * awaits it can be sure the next render is translated.
 */
export async function setLocale(code: string | null | undefined): Promise<void> {
  const next = code && knownLocales().includes(code) ? code : FALLBACK_LOCALE;
  if (next !== FALLBACK_LOCALE) await loadBundle(next);
  locale.value = next;
  revision.value++;
  prefStorage.set(LOCALE_KEY, next);
  document.documentElement.lang = next.split('_')[0]!;
}

function lookup(bundle: Bundle | undefined, key: string): Node | undefined {
  if (!bundle) return undefined;
  let node: Node | undefined = bundle;
  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = node[segment];
  }
  return node;
}

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

export type Params = Record<string, string | number>;

/**
 * The English text for a key, or the key itself.
 *
 * Returning the key rather than an empty string is deliberate: a missing
 * translation should be obvious in the interface and greppable in a
 * screenshot, not an invisible hole.
 *
 * A key whose value is an object is treated as a plural form and needs
 * `params.count` — see `tc`.
 */
function translate(key: string, params?: Params): string {
  const active = lookup(bundles.get(locale.value), key);
  const value = active ?? lookup(bundles.get(FALLBACK_LOCALE), key);

  if (typeof value === 'string') return interpolate(value, params);

  if (value && typeof value === 'object' && params && typeof params.count === 'number') {
    const rule = new Intl.PluralRules(bcp47(locale.value)).select(params.count);
    const form = value[rule] ?? value.other;
    if (typeof form === 'string') return interpolate(form, params);
  }

  return key;
}

/** `en_GB` → `en-GB`, which is what every `Intl` constructor wants. */
export function bcp47(code: string): string {
  return code.replace('_', '-');
}

/**
 * Reactive translation.
 *
 * Reading `revision` inside `t` is what makes a component re-render when a
 * bundle finishes loading: the value is never used, but the dependency is.
 */
export function useI18n() {
  const t = (key: string, params?: Params): string => {
    void revision.value;
    return translate(key, params);
  };

  /** Plural form for `count`, with the count available as `{count}`. */
  const tc = (key: string, count: number, params?: Params): string =>
    t(key, { ...params, count });

  /** A number in the active locale — grouping separators, not raw digits. */
  const n = (value: number, options?: Intl.NumberFormatOptions): string => {
    void revision.value;
    return new Intl.NumberFormat(bcp47(locale.value), options).format(value);
  };

  /** A date in the active locale. */
  const d = (value: Date | string | number, options?: Intl.DateTimeFormatOptions): string => {
    void revision.value;
    const date = value instanceof Date ? value : new Date(value);
    return new Intl.DateTimeFormat(bcp47(locale.value), options).format(date);
  };

  return {
    t,
    tc,
    n,
    d,
    setLocale,
    locale: computed(() => locale.value),
    locales: knownLocales,
  };
}

/**
 * The non-reactive translator, for engine code that draws into a canvas and
 * has no Vue reactivity to hang off — name tags over hulls, for instance.
 */
export const t = (key: string, params?: Params): string => translate(key, params);

// Pick the opening language before the first component mounts; the account's
// stored preference overrides it once the profile has been fetched.
void setLocale(initialLocale());
